import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Shield, FileSearch, BarChart3, ArrowRight, CheckCircle, Users, AlertTriangle } from 'lucide-react';
import SEO from '../components/SEO';

const HomePage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <img src="https://customer-assets.emergentagent.com/job_33cfb0fa-a3e3-4c94-8cb4-e70f64f9a98c/artifacts/9kyidihk_PlainEye.PNG" alt="Pattern Recognition" className="w-8 h-8 object-contain" />,
      title: "Pattern Recognition",
      description: "Advanced AI identifies patterns of coercive control across all communication channels"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Expert Analysis",
      description: "Multiple AI models analyze content using established psychological frameworks"
    },
    {
      icon: <FileSearch className="w-8 h-8" />,
      title: "Secure PDF Processing",
      description: "Process structured PDF documents with encrypted analysis and protected handling"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Professional Reports",
      description: "Generate detailed pattern reports with references, risk scoring, and expert analysis"
    }
  ];

  return (
    <div className="page-transition">
      <SEO 
        title="Document Pattern Analysis for Coercive Control"
        description="AI-powered document analysis that identifies coercive control patterns in communications. Transform emails, texts, and documents into clear, professional reports."
        keywords="coercive control, pattern analysis, document analysis, communication patterns, behavioral patterns, legal documentation, abuse documentation"
        canonical="/"
      />
      {/* Navigation */}
      <nav className="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_33cfb0fa-a3e3-4c94-8cb4-e70f64f9a98c/artifacts/9kyidihk_PlainEye.PNG" 
                alt="Coercive Control Patterns Logo" 
                className="w-10 h-10 object-contain"
              />
              <span className="text-xl font-bold gradient-text serif-display">Coercive Control Patterns</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => navigate('/signin')} className="btn-primary">
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="text-left">
              <h1 className="text-3xl lg:text-4xl font-bold mb-2 serif-display leading-tight">
                <span className="gradient-text">Make Patterns Measurable</span>
              </h1>
              <p className="text-sm text-gray-200 mb-4 leading-relaxed italic">
                You don't have to prove it alone. We help you make patterns visible — simply, securely, and privately.
              </p>
              <p className="text-base text-gray-300 mb-4 leading-relaxed">
                Transform difficult communications into clear, structured reports that identify recurring behavioral patterns. 
                Professional analysis powered by advanced AI technology.
              </p>
            </div>
            
            <div className="relative lg:mt-3">
              <div className="glass rounded-2xl p-4 float-animation relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_33cfb0fa-a3e3-4c94-8cb4-e70f64f9a98c/artifacts/10som4zg_FirstPageImage.png"
                  alt="Making patterns measurable"
                  className="w-full h-auto max-h-64 object-contain rounded-xl mx-auto"
                />
              </div>
            </div>
          </div>
          
          {/* How It Works - Full Screen Width */}
          <div className="mt-0 mb-6 bg-slate-800/50 rounded-xl p-4 backdrop-blur-sm border border-slate-700/30">
            <h3 className="text-xl font-bold mb-4 text-white text-center">How It Works</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="text-center -mt-1">
                <button 
                  onClick={() => navigate('/signin')}
                  className="w-full hover:bg-slate-700/30 rounded-lg transition-colors group"
                >
                  <div className="w-12 h-12 bg-purple-900/50 rounded-full flex items-center justify-center mx-auto mb-2 border-2 border-purple-400/30 group-hover:bg-purple-800/50 group-hover:border-purple-300/50 transition-colors">
                    <span className="text-lg font-bold text-purple-400">1</span>
                  </div>
                  <h4 className="text-xl font-semibold mb-3 text-white">Sign In</h4>
                  <p className="text-lg text-gray-300 leading-relaxed">
                    Create account for dashboard access
                  </p>
                  <p className="text-sm text-gray-400 italic mt-2 px-2">
                    (Your email is for account access only. No marketing emails. No spam. Ever.)
                  </p>
                </button>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-900/50 rounded-full flex items-center justify-center mx-auto mb-2 border-2 border-purple-400/30">
                  <span className="text-lg font-bold text-purple-400">2</span>
                </div>
                <h4 className="text-xl font-semibold mb-3 text-white">Choose Package</h4>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Select package for document size ($39.99-$89.99)
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-900/50 rounded-full flex items-center justify-center mx-auto mb-2 border-2 border-purple-400/30">
                  <span className="text-lg font-bold text-purple-400">3</span>
                </div>
                <h4 className="text-xl font-semibold mb-3 text-white">Upload Content</h4>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Securely upload your PDF communication records.
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-900/50 rounded-full flex items-center justify-center mx-auto mb-2 border-2 border-purple-400/30">
                  <span className="text-lg font-bold text-purple-400">4</span>
                </div>
                <h4 className="text-xl font-semibold mb-3 text-white">AI Analysis</h4>
                <p className="text-lg text-gray-300 leading-relaxed">
                  AI analyzes for control patterns
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-900/50 rounded-full flex items-center justify-center mx-auto mb-2 border-2 border-purple-400/30">
                  <span className="text-lg font-bold text-purple-400">5</span>
                </div>
                <h4 className="text-xl font-semibold mb-3 text-white">Get Report</h4>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Download professional PDF report
                </p>
                <p className="text-sm text-gray-400 mt-1 italic">
                  (Reports are deleted after download.)
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="text-left">
              {/* Check Your Results section removed for live version */}

              {/* Important Disclaimer */}
              <div className="p-3 bg-amber-900/20 border border-amber-600/30 rounded-lg mb-4">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-amber-300 mb-2">Important Disclaimer</h4>
                    <p className="text-amber-200">
                      This tool uses advanced AI pattern detection to highlight behaviors linked to coercive control. While no AI system can guarantee 100% accuracy, the analysis is designed to be highly reliable and consistent across large sets of communication.
                    </p>
                    <p className="text-amber-200 mt-2">
                      Reports should be viewed as supportive documentation — a way to make patterns measurable — not as final proof on their own. We recommend sharing results alongside your own records and professional guidance.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4 text-xs text-gray-400">
                <div className="flex items-center space-x-1">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Secure & Confidential</span>
                </div>
                <div className="flex items-center space-x-1">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Detailed Pattern Reports</span>
                </div>
              </div>
            </div>
            <div>
              {/* Empty placeholder for grid balance */}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 serif-display text-white">Advanced Analysis Technology</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our AI-powered platform analyzes communication patterns using established psychological 
              frameworks to identify coercive control behaviors.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="card text-center group">
                <div className="text-purple-400 mb-4 flex justify-center group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Analysis Categories */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 serif-display text-white">Comprehensive Analysis Categories</h2>
            <p className="text-xl text-gray-300">
              Our system analyzes 19 established categories of coercive control
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Gaslighting", "Image Management", "Narrative Control", "Financial Control",
              "Stalking / Surveillance", "Manipulation", "Undermining", "Interference in Parenting",
              "Guilt-Tripping", "Interference with Child's Relationship with Other Parent", "Threats / Intimidation",
              "DARVO Tactics", "Emotional Abuse / Belittling", "DVRO-Relevant Behaviors", "Conflict Instigation",
              "Isolation", "Micromanagement / Rules", "Use of Children as Leverage", "Intermittent Reinforcement"
            ].map((category, index) => (
              <div key={index} className="bg-slate-700 rounded-lg p-4 text-center border border-slate-600">
                <span className="font-medium text-gray-200">{category}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-indigo-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6 serif-display">
            Ready to Make Patterns Measurable?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Join thousands of professionals who trust our analysis to identify patterns of coercive control.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/signin')}
              className="bg-white text-purple-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-colors"
            >
              Get Started Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_33cfb0fa-a3e3-4c94-8cb4-e70f64f9a98c/artifacts/xo8lt1hg_PlainEye.PNG" 
                  alt="Coercive Control Patterns Logo" 
                  className="w-6 h-6 object-contain"
                />
                <span className="text-lg font-bold">Coercive Control Patterns</span>
              </div>
              <p className="text-gray-400">
                Making invisible patterns of coercive control visible through advanced AI analysis.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Analysis</li>
                <li>Reports</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Documentation</li>
                <li>
                  <a 
                    href="/privacy?testing=enabled"
                    className="hover:text-purple-400 transition-colors cursor-pointer underline"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a 
                    href="/terms?testing=enabled"
                    className="hover:text-purple-400 transition-colors cursor-pointer underline"
                  >
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a 
                    href="/about?testing=enabled"
                    className="hover:text-purple-400 transition-colors cursor-pointer underline"
                  >
                    About
                  </a>
                </li>
                <li>
                  <a 
                    href="/contact?testing=enabled"
                    className="hover:text-purple-400 transition-colors cursor-pointer underline"
                  >
                    Contact
                  </a>
                </li>
                <li>Blog</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Coercive Control Patterns. All rights reserved. For informational and educational purposes only, not legal advice.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;